import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';
import { useAuthStore } from '../store/authStore';

interface UseRenewalConfirmationReturn {
  showConfirmationModal: boolean;
  confirmationMessage: string;
  closeConfirmationModal: () => void;
}

/**
 * Hook to handle academic renewal confirmation from URL parameters
 * Detects when user is redirected from email confirmation link
 */
export const useRenewalConfirmation = (): UseRenewalConfirmationReturn => {
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const setUser = useAuthStore(s => s.setUser);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const renewalConfirmed = urlParams.get('renewal_confirmed');
    const message = urlParams.get('message');

    if (renewalConfirmed === 'true') {
      setShowConfirmationModal(true);
      setConfirmationMessage(message || 'Your academic license renewal has been confirmed successfully');

      // Clean up URL parameters without triggering a page reload
      const cleanUrl = window.location.pathname;
      navigate(cleanUrl, { replace: true });

      // Load new custom attributes (academic_renewed_at, next_payment_due, …) into the app session
      void (async () => {
        try {
          const session = await fetchAuthSession({ forceRefresh: true });
          const payload = session.tokens?.idToken?.payload;
          if (!payload) return;
          const current = await getCurrentUser().catch(() => null);
          setUser({
            ...payload,
            ...(current || {}),
          } as any);
        } catch {
          /* Sin sesión aún: el próximo login cargará los claims nuevos */
        }
      })();
    }
  }, [location.search, navigate, setUser]);

  const closeConfirmationModal = () => {
    setShowConfirmationModal(false);
    setConfirmationMessage('');
  };

  return {
    showConfirmationModal,
    confirmationMessage,
    closeConfirmationModal,
  };
};
